import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { DashboardView } from '../features/dashboard/dashboard-view';
import { useOperationsClient } from '../lib/api/operations-client-context';
import { describeApiError } from '../lib/session/describe-error';

/** `YYYY-MM-DD`, and nothing else. A junk `?date` is dropped rather than sent on. */
const LOCAL_DATE = /^\d{4}-\d{2}-\d{2}$/;

interface DashboardSearch {
  date?: string;
}

function parseSearch(search: Record<string, unknown>): DashboardSearch {
  const raw = String(search['date'] ?? '');
  return LOCAL_DATE.test(raw) ? { date: raw } : {};
}

export const Route = createFileRoute('/')({
  validateSearch: parseSearch,
  component: DashboardPage,
});

/**
 * `/` — the clinic dashboard (MASTER_PLAN M11.1, FR-ADMIN-02, `Docs/API.md` §2.5).
 *
 * The route is the only thing here that knows about the network: it fetches and hands
 * `<DashboardView>` plain data (rules §17.1).
 *
 * **M11.1 put this on `GET /admin/dashboard`.** The integrations strip used to be fetched
 * separately from `GET /admin/integrations` because the dashboard endpoint did not exist;
 * it now comes back in the same payload, so the strip and the counts are one moment in
 * time and the route makes one request.
 *
 * `?date` is the clinic-local day, in the URL, so "the 3rd looked like this" is a link a
 * manager can send. The **browser's own date is never sent**: an omitted `?date` means
 * "today in the clinic's zone", which the server decides — a receptionist covering from
 * another time zone still opens the clinic's day (API.md §1.7).
 *
 * `placeholderData` keeps the previous day on screen while the next one loads, so picking
 * a date does not collapse the page into a spinner and lose the reader's place; the header
 * says it is refreshing instead.
 */
function DashboardPage() {
  const client = useOperationsClient();
  const { date } = Route.useSearch();
  const navigate = useNavigate();

  const dashboard = useQuery({
    queryKey: ['dashboard', date ?? 'today'],
    queryFn: () => client.getDashboard(date),
    placeholderData: keepPreviousData,
  });

  return (
    <DashboardView
      data={dashboard.data}
      isLoading={dashboard.isPending}
      isRefreshing={dashboard.isFetching && !dashboard.isPending}
      error={dashboard.isError ? describeApiError(dashboard.error) : undefined}
      onRetry={() => {
        void dashboard.refetch();
      }}
      onRefresh={() => {
        void dashboard.refetch();
      }}
      {...(date === undefined ? {} : { date })}
      onDateChange={(next) => {
        void navigate({
          to: '/',
          search: LOCAL_DATE.test(next) ? { date: next } : {},
          replace: true,
        });
      }}
      renderLink={(to, children, className, search): ReactNode => (
        <Link to={to} search={search} className={className}>
          {children}
        </Link>
      )}
    />
  );
}
